//material-ui
import { Alert, Autocomplete, CircularProgress, FilterOptionsState, TextField } from "@mui/material";
import { ClientError } from "graphql-request";
import { Fragment, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useTopActionPaper, { type SelectTopAction } from "~/components/select/useTopActionPaper";
import { type LocationDocument, LocationStatus, LocationType } from "~/gql/graphqlBase.generated";
import toLocationAddress from "~/helpers/toLocationAddress";
import { anyToString } from "~/helpers/toString";
import { useLogError } from "~/hooks/useLogError";
import { useGetSiteLocationsBasicQuery } from "~/services/endpoints/siteSelectApi";

type SiteOption = Pick<LocationDocument, "id" | "name">;

interface SelectSiteProps {
	/** When true, renders a clearable "All Sites" option (null value) for use as an optional filter. */
	allowAllSites?: boolean;
	/** Autofocus the input on mount. Defaults to true (standalone gate/modal usage); pass false when embedded among other form fields. */
	autoFocus?: boolean;
	/**
	 * Auto-commit the only option when exactly one site is available. Defaults to true (create/gate usage).
	 * Pass false where an implicit commit would be a side effect — e.g. an edit control that mutates on change.
	 */
	autoSelectSingle?: boolean;
	disabled?: boolean;
	errorText?: string;
	handleInputChange: (val: LocationDocument | null) => void;
	helperText?: ReactNode;
	initialValue?: SiteOption;
	/** Optional override. Defaults to a per-instance unique id via React useId so two instances on one page do not collide. */
	inputId?: string;
	/** Input label. Defaults to "Site". */
	label?: string;
	/** Auto-commit the only active site when exactly one exists, even while the list is closed. Opt-in. */
	preselectSingle?: boolean;
	/** Marks the field required. */
	required?: boolean;
	topAction?: SelectTopAction;
}

const SelectSite = ({
	allowAllSites,
	autoFocus = true,
	autoSelectSingle = true,
	disabled,
	errorText,
	handleInputChange,
	helperText,
	initialValue,
	inputId,
	label,
	preselectSingle,
	required,
	topAction
}: SelectSiteProps) => {
	const { t } = useTranslation("common");
	const [sites, setSites] = useState<LocationDocument[]>([]);
	const [hasNextPage, setHasNextPage] = useState(true);
	const [cursor, setCursor] = useState<string | null>(null);

	const [logError] = useLogError();

	const [acVal, setAcVal] = useState<SiteOption | null>(initialValue || null);
	const [acOpen, setAcOpen] = useState(false);
	const topActionSlots = useTopActionPaper(topAction, () => setAcOpen(false));
	const generatedId = useId();
	const resolvedId = inputId ?? generatedId;

	const { data, error, isLoading, refetch } = useGetSiteLocationsBasicQuery(
		{ after: cursor, first: 50 },
		{ skip: !hasNextPage }
	);

	useEffect(() => {
		if (data?.siteLocations?.edges && data.siteLocations.edges.length > 0) {
			const nodes: LocationDocument[] = data.siteLocations.edges
				.filter((edge) => edge.node.status === LocationStatus.Active)
				.map((edge) => ({
					address: toLocationAddress(edge.node.address),
					created: edge.node.created,
					erpId: edge.node.erpId,
					id: edge.node.siteLocationId,
					locationType: LocationType.Site,
					name: edge.node.name,
					parentLocationId: "",
					siteParentId: edge.node.siteLocationId,
					status: edge.node.status,
					tenantId: "",
					updated: edge.node.updated
				}));
			setSites((prevData) => [...prevData, ...nodes]);
			setHasNextPage(data.siteLocations.pageInfo.hasNextPage);
			setCursor(data.siteLocations.pageInfo.endCursor || null);
		}
	}, [data]);

	useEffect(() => {
		if (hasNextPage) {
			refetch();
		}
	}, [hasNextPage, refetch]);

	const preselected = useRef(false);
	useEffect(() => {
		if (!preselectSingle || preselected.current || hasNextPage || sites.length !== 1) return;
		preselected.current = true;
		setAcVal((current) => current ?? sites[0]);
		handleInputChange(sites[0]);
	}, [handleInputChange, hasNextPage, preselectSingle, sites]);

	const acInput = useRef<HTMLInputElement>(null);

	const acFilter = (options: SiteOption[], state: FilterOptionsState<SiteOption>) => {
		const result = options.filter(
			(option) => `${option.name}`.toLowerCase().indexOf(state.inputValue.toLowerCase()) > -1
		);
		if (autoSelectSingle && result.length === 1 && result[0].id !== acVal?.id) {
			// Defer state updates to avoid "setState during render" React warning
			// (acFilter runs synchronously inside Autocomplete's render).
			queueMicrotask(() => {
				setAcVal(result[0]);
				handleInputChange(sites.find((site) => site.id === result[0].id) ?? null);
				setAcOpen(false);
			});
		}
		return result;
	};

	if (error) {
		const clientError = error as ClientError;
		const errorMessage = t("sitePicker.loadError", { message: anyToString(clientError) });
		logError({ code: "SelectSiteError", detail: clientError, message: anyToString(clientError.message) });
		return <Alert severity="error">{errorMessage}</Alert>;
	}

	return (
		<Autocomplete<SiteOption>
			clearOnEscape={allowAllSites}
			disabled={disabled}
			filterOptions={acFilter}
			getOptionLabel={(option) => (option.name ? `${option.name}` : "")}
			id={resolvedId}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			loading={isLoading}
			onChange={(_e, v) => {
				setAcVal(v);
				handleInputChange(v ? (sites.find((site) => site.id === v.id) ?? null) : null);
			}}
			onClose={() => setAcOpen(false)}
			onOpen={() => setAcOpen(true)}
			open={acOpen}
			options={sites}
			renderInput={(params) => (
				<TextField
					{...params}
					autoFocus={autoFocus}
					error={Boolean(errorText)}
					required={required}
					helperText={errorText ?? helperText}
					label={label ?? t("sitePicker.label")}
					ref={acInput}
					slotProps={{
						input: {
							...params.InputProps,
							endAdornment: (
								<Fragment>
									{isLoading ? <CircularProgress color="inherit" size={20} /> : null}
									{params.InputProps.endAdornment}
								</Fragment>
							)
						}
					}}
					variant="outlined"
				/>
			)}
			slots={topActionSlots}
			sx={{ width: "100%" }}
			value={acVal}
		/>
	);
};

export default SelectSite;
